/**
 * referral.ts — partner referral attribution capture (client side).
 *
 * A consultant shares a link like https://karr.pro/?ref=ABCD1234. We capture
 * that code on FIRST touch (so a later marketing visit can't overwrite it) and
 * stash it in localStorage, which survives the Google OAuth redirect round-trip
 * (same origin). Onboarding reads it and passes it to create_org, which records
 * the attribution server-side. Codes expire after 90 days.
 *
 * @see supabase/migrations/00200_partner_referral.sql (attribute_referral)
 */

const KEY = 'karr_ref';
const MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000; // 90 days

/** Read ?ref= from the current URL and store it (first-touch wins). Call on app load. */
export function captureReferral(): void {
    try {
        const raw = new URLSearchParams(window.location.search).get('ref');
        if (!raw) return;
        const code = raw.trim().toUpperCase();
        if (!/^[A-Z0-9]{4,16}$/.test(code)) return;
        const existing = localStorage.getItem(KEY);
        if (existing) {
            try {
                const p = JSON.parse(existing) as { code?: string; ts?: number };
                if (p?.code && Date.now() - (p.ts || 0) < MAX_AGE_MS) return; // keep first-touch
            } catch { /* fall through and overwrite a corrupt value */ }
        }
        localStorage.setItem(KEY, JSON.stringify({ code, ts: Date.now() }));
    } catch { /* SSR / storage disabled — ignore */ }
}

/** The stored referral code, or null if absent/expired. */
export function getStoredReferral(): string | null {
    try {
        const raw = localStorage.getItem(KEY);
        if (!raw) return null;
        const p = JSON.parse(raw) as { code?: string; ts?: number };
        if (!p?.code) return null;
        if (Date.now() - (p.ts || 0) > MAX_AGE_MS) { localStorage.removeItem(KEY); return null; }
        return p.code;
    } catch { return null; }
}

/** Forget the stored code (call after it's been attributed at org creation). */
export function clearStoredReferral(): void {
    try { localStorage.removeItem(KEY); } catch { /* ignore */ }
}
